/**
 * Typed wrapper for the citizen cash-out token endpoints on
 * service-financial-services (port 3110).
 *
 * Endpoints:
 *   - POST /v1/cash-out/request           — wallet debit + 6-digit token issued
 *   - GET  /v1/cash-out/tokens/me         — list citizen's tokens
 *   - POST /v1/cash-out/tokens/:id/cancel — cancel + refund wallet
 *
 * RBAC:
 *   - request, cancel → CashOut/request-own
 *   - tokens/me       → CashOut/read-own
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface RequestCashOutBody {
  readonly amount: string;
  readonly currency?: string;
  readonly idempotencyKey?: string;
}

export interface CashOutToken {
  readonly id: string;
  readonly token: string;
  readonly amount: string;
  readonly currency: string;
  readonly status: 'PENDING' | 'REDEEMED' | 'EXPIRED' | 'CANCELLED';
  readonly expiresAt: string;
  readonly createdAt: string;
  readonly redeemedAt: string | null;
  readonly cancelledAt: string | null;
  readonly redeemedAtOutlet: string | null;
}

export interface CashOutTokenWithBalance extends CashOutToken {
  readonly walletBalance: string;
}

export interface ListMyCashOutTokensResponse {
  readonly items: readonly CashOutToken[];
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function requestCashOut(
  body: RequestCashOutBody,
  options: CallOpts = {},
): Promise<CashOutTokenWithBalance> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CashOutTokenWithBalance>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/cash-out/request',
    body,
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function listMyCashOutTokens(
  options: CallOpts = {},
): Promise<ListMyCashOutTokensResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<ListMyCashOutTokensResponse>({
    method: 'GET',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: '/v1/cash-out/tokens/me',
    bearerToken: bearerOf(options),
    ...rest,
  });
}

export async function cancelCashOutToken(
  id: string,
  options: CallOpts = {},
): Promise<CashOutTokenWithBalance> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<CashOutTokenWithBalance>({
    method: 'POST',
    baseUrl: env.serviceFinancialServicesBaseUrl,
    path: `/v1/cash-out/tokens/${encodeURIComponent(id)}/cancel`,
    body: {},
    bearerToken: bearerOf(options),
    ...rest,
  });
}
